/**
 * Geographic constants for the working area: Tel Aviv-Yafo, Givatayim and Ramat Gan.
 *
 * The constant is still called TLV_CENTER_BBOX for the sake of the many call sites that
 * import it; the name is now narrower than what it holds.
 */

/**
 * The working area: [south, west, north, east].
 *
 * The whole of Tel Aviv-Yafo — Jaffa in the south, Ramat Aviv and Tel Baruch across the
 * Yarkon in the north — plus Givatayim and Ramat Gan across the Ayalon in the east.
 *
 * This started as Tel Aviv centre only, on the argument that Ramat Aviv and the far south
 * were "a different job with different economics" and would dilute the grid. That
 * reasoning does not survive contact with the model it produced. Dilution was never the
 * risk: the score is computed per cell from venues reachable within six minutes, so a
 * quiet cell in Ramat Aviv scores quietly and costs the busy cells nothing. What the
 * narrow box actually did was refuse to answer the question "is it worth riding north
 * tonight?" — and a demand map that cannot be asked that is answering a smaller question
 * than the rider is asking.
 *
 * Three things this box does not paper over:
 *
 * - **The Ayalon and the Yarkon are real barriers.** Both are routed around rather than
 *   over, so cells that are close in metres and far in minutes read as far — which is the
 *   entire reason travel-time routing exists here rather than a radius.
 * - **The north-east corner clips Bnei Brak**, whose Shabbat pattern is nothing like Tel
 *   Aviv's. That is carried by real opening hours, not by an assumption.
 * - **The box includes a lot of sea and park.** Those cells reach no venues and are
 *   dropped by the matrix stage rather than rendered as dead ground.
 */
export const TLV_CENTER_BBOX: [number, number, number, number] = [
  32.015, // south — through Ajami and Jabaliya to the Bat Yam edge
  34.745, // west  — the sea, wide enough for the Jaffa headland
  32.14, // north — Ramat Aviv, the university and Tel Baruch
  34.845, // east  — across the Ayalon, through Givatayim to eastern Ramat Gan
];

/**
 * H3 resolution 9 gives cells of roughly 0.1 km2 (~174 m edge). Over the bbox above
 * that is a few hundred cells: fine enough that "go two blocks north" is expressible,
 * coarse enough that the travel-time matrix stays small and the map stays readable on
 * a phone at arm's length.
 */
export const H3_RESOLUTION = 9;

/**
 * Venues further than this from a cell contribute negligibly after distance decay, so
 * the travel-time matrix drops them. Six minutes is about 1.5 km of real Tel Aviv
 * ebike riding once traffic lights are accounted for.
 */
export const TRAVEL_TIME_CUTOFF_S = 360;

/**
 * Assumed ebike speed on the road network, in metres per second.
 *
 * 5.0 m/s is 18 km/h. That is deliberately below the legal 25 km/h assist cap: it is an
 * average including junctions, lights, pedestrians on Rothschild, and the time spent
 * looking for the entrance. Tuning this changes the effective reach radius, so it lives
 * here rather than being scattered through the routing code.
 */
export const EBIKE_SPEED_MPS = 5.0;

/** Israel is UTC+2 in winter, UTC+3 during DST. */
export const TLV_TIMEZONE = 'Asia/Jerusalem';

export function isInBbox(
  lat: number,
  lon: number,
  bbox: [number, number, number, number] = TLV_CENTER_BBOX,
): boolean {
  const [south, west, north, east] = bbox;
  return lat >= south && lat <= north && lon >= west && lon <= east;
}

/** Great-circle distance in metres. Used for sanity checks and fixture generation. */
export function haversineMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
